import { useEffect, useState } from "react";
import axios from "axios";
import { LogIn, LogOut, Lock, Mail, MapPin, Calendar, Phone, MessageSquare, Trash2, AlertCircle } from "lucide-react";
import { LOGO_TRANSPARENT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "vr_admin_token";

function fmtError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  // Login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Bookings
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) {
          localStorage.removeItem(STORAGE_KEY);
          setToken("");
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!user || !token) return;
    let cancelled = false;
    async function load() {
      setLoadingBookings(true);
      try {
        const { data } = await axios.get(`${API}/admin/bookings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setBookings(data || []);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem(STORAGE_KEY);
          setToken("");
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoadingBookings(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user, token]);

  async function onLogin(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(STORAGE_KEY, data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      setPassword("");
    } catch (err) {
      setError(fmtError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  }

  function onLogout() {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setUser(null);
    setBookings([]);
  }

  async function onDelete(id) {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings((b) => b.filter((x) => x.id !== id));
    } catch (err) {
      alert(fmtError(err.response?.data?.detail) || "Failed to delete.");
    }
  }

  if (checking) {
    return (
      <div className="min-h-screen bg-ink-900 text-sand-50 flex items-center justify-center">
        <img src={LOGO_TRANSPARENT} alt="Villa Reset Ibiza" className="w-40 opacity-70 animate-pulse" />
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className="min-h-screen bg-ink-900 text-sand-50 flex items-center justify-center px-6 py-16"
        data-testid="admin-login-screen"
      >
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-12">
            <img src={LOGO_TRANSPARENT} alt="Villa Reset Ibiza" className="w-52 mb-6" />
            <span className="text-[10px] tracking-[0.4em] uppercase text-gold">
              Private Access
            </span>
          </div>

          <form onSubmit={onLogin} className="flex flex-col gap-7" data-testid="admin-login-form" noValidate>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] tracking-[0.3em] uppercase text-sand-300">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                data-testid="admin-email-input"
                className="w-full bg-transparent border-b border-sand-50/25 py-3 text-base focus:outline-none focus:border-gold transition-colors"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] tracking-[0.3em] uppercase text-sand-300">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="admin-password-input"
                className="w-full bg-transparent border-b border-sand-50/25 py-3 text-base focus:outline-none focus:border-gold transition-colors"
              />
            </label>

            {error && (
              <div
                className="flex items-start gap-3 text-sm text-red-300 border border-red-300/30 px-4 py-3"
                data-testid="admin-login-error"
              >
                <AlertCircle size={16} strokeWidth={1.4} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 inline-flex items-center justify-center gap-3 bg-sand-50 text-ink-900 px-9 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-gold transition-colors disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              <LogIn size={14} strokeWidth={1.4} />
              <span>{submitting ? "Signing in…" : "Enter"}</span>
            </button>
          </form>

          <a
            href="/"
            className="block text-center mt-10 text-[10px] tracking-[0.3em] uppercase text-sand-50/50 hover:text-gold"
            data-testid="admin-back-home"
          >
            ← Back to site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50 text-ink-900" data-testid="admin-dashboard">
      <header className="bg-ink-900 text-sand-50 border-b border-sand-50/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3" data-testid="admin-logo-home">
            <img src={LOGO_TRANSPARENT} alt="Villa Reset Ibiza" className="h-12" />
          </a>
          <div className="flex items-center gap-4 md:gap-6">
            <span className="hidden md:inline text-[10px] tracking-[0.3em] uppercase text-gold">
              {user.email}
            </span>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 border border-sand-50/30 text-sand-50 px-4 py-2 text-[10px] uppercase tracking-[0.3em] hover:bg-sand-50 hover:text-ink-900 transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={12} strokeWidth={1.4} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <span className="block text-[10px] tracking-[0.4em] uppercase text-gold-dark mb-4">
              — Concierge Desk
            </span>
            <h1 className="font-serif font-light leading-[1.05] tracking-tight text-3xl md:text-5xl">
              Booking Requests
            </h1>
          </div>
          <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-ink-500">
            <Lock size={12} strokeWidth={1.4} />
            <span data-testid="admin-bookings-count">
              {bookings.length} {bookings.length === 1 ? "request" : "requests"}
            </span>
          </div>
        </div>

        {loadingBookings ? (
          <div className="py-20 text-center text-ink-500 text-sm" data-testid="admin-loading">
            Loading…
          </div>
        ) : bookings.length === 0 ? (
          <div className="py-20 text-center text-ink-500" data-testid="admin-empty">
            <p className="font-serif text-2xl md:text-3xl mb-2">No requests yet.</p>
            <p className="text-sm">New villa service requests will appear here.</p>
          </div>
        ) : (
          <ul className="border-t border-sand-200" data-testid="admin-bookings-list">
            {bookings.map((b) => (
              <li
                key={b.id}
                className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 py-8 md:py-10 border-b border-sand-200"
                data-testid={`admin-booking-${b.id}`}
              >
                <div className="lg:col-span-3 flex flex-col gap-2">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-gold-dark">
                    {formatDate(b.created_at)}
                  </span>
                  <span className="font-serif text-2xl md:text-3xl text-ink-900 leading-tight">
                    {b.name}
                  </span>
                  <span className="text-[11px] tracking-[0.25em] uppercase text-ink-500">
                    {b.service}
                  </span>
                </div>

                <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  <a
                    href={`mailto:${b.email}`}
                    className="inline-flex items-center gap-2 text-ink-700 hover:text-gold-dark break-all"
                  >
                    <Mail size={14} strokeWidth={1.4} className="text-sand-400 shrink-0" />
                    {b.email}
                  </a>
                  <a
                    href={`https://wa.me/${b.phone.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-ink-700 hover:text-gold-dark"
                  >
                    <Phone size={14} strokeWidth={1.4} className="text-sand-400 shrink-0" />
                    {b.phone}
                  </a>
                  <span className="inline-flex items-center gap-2 text-ink-700">
                    <MapPin size={14} strokeWidth={1.4} className="text-sand-400 shrink-0" />
                    {b.location}
                  </span>
                  <span className="inline-flex items-center gap-2 text-ink-700">
                    <Calendar size={14} strokeWidth={1.4} className="text-sand-400 shrink-0" />
                    {b.date || "—"}
                  </span>
                  {b.message && (
                    <div className="sm:col-span-2 inline-flex items-start gap-2 text-ink-500 italic">
                      <MessageSquare size={14} strokeWidth={1.4} className="text-sand-400 shrink-0 mt-1" />
                      <p className="whitespace-pre-line">{b.message}</p>
                    </div>
                  )}
                </div>

                <div className="lg:col-span-3 flex lg:justify-end items-start">
                  <button
                    onClick={() => onDelete(b.id)}
                    className="group inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-ink-500 hover:text-red-600 transition-colors"
                    data-testid={`admin-delete-${b.id}`}
                  >
                    <Trash2 size={12} strokeWidth={1.4} />
                    <span>Delete</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
